import { getCurrentServer, getCurrentSiteHost } from "./globalData";

const LOG_VALUE_LIMIT = 600;
const LOG_BODY_LIMIT = 1200;
const LOG_STACK_LIMIT = 4000;

function isObjectLike(value) {
  return value !== null && typeof value === "object";
}

export function getHeaderValue(headers, key) {
  if (!headers || !key) {
    return "";
  }

  if (typeof headers.get === "function") {
    return headers.get(key) || headers.get(String(key).toLowerCase()) || "";
  }

  return headers[key] || headers[String(key).toLowerCase()] || "";
}

export function truncateLogValue(value, maxLength = LOG_VALUE_LIMIT) {
  if (value === undefined || value === null) {
    return "";
  }

  const stringValue = String(value);
  if (stringValue.length <= maxLength) {
    return stringValue;
  }

  return `${stringValue.slice(0, maxLength)}...[truncated]`;
}

export function safeStringifyForLog(value, maxLength = LOG_BODY_LIMIT) {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  try {
    const stringValue = typeof value === "string" ? value : JSON.stringify(value);
    return truncateLogValue(stringValue, maxLength);
  } catch (error) {
    return truncateLogValue(value, maxLength);
  }
}

function buildUpstreamUrl(baseURL = "", requestUrl = "") {
  const normalizedBaseURL = String(baseURL || "").trim();
  const normalizedRequestUrl = String(requestUrl || "").trim();

  if (!normalizedBaseURL) {
    return normalizedRequestUrl;
  }

  try {
    return new URL(normalizedRequestUrl, normalizedBaseURL).toString();
  } catch (error) {
    return `${normalizedBaseURL}${normalizedRequestUrl}`;
  }
}

function buildRequestLogId(headers = {}, requestEvent = null) {
  const requestId =
    getHeaderValue(headers, "x-request-id")
    || getHeaderValue(headers, "x-b3-traceid")
    || getHeaderValue(headers, "traceparent")
    || getHeaderValue(headers, "x-amzn-trace-id");

  if (requestId) {
    return truncateLogValue(requestId, LOG_VALUE_LIMIT);
  }

  if (!requestEvent) {
    return "";
  }

  if (!requestEvent.context.__requestLogId) {
    requestEvent.context.__requestLogId = `ssr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }

  return requestEvent.context.__requestLogId;
}

function summarizeResponseForLog(responseData) {
  if (responseData === undefined || responseData === null) {
    return {};
  }

  if (isObjectLike(responseData)) {
    const summary = {};

    if (typeof responseData.success === "boolean") {
      summary.responseSuccess = responseData.success;
    }

    if (responseData.status !== undefined && responseData.status !== null && responseData.status !== "") {
      summary.responseBusinessStatus = truncateLogValue(responseData.status, LOG_VALUE_LIMIT);
    }

    if (responseData.code !== undefined && responseData.code !== null && responseData.code !== "") {
      summary.responseBusinessCode = truncateLogValue(responseData.code, LOG_VALUE_LIMIT);
    }

    const businessMessage = responseData.msg || responseData.message || responseData.error;
    if (businessMessage) {
      summary.responseBusinessMessage = truncateLogValue(businessMessage, LOG_VALUE_LIMIT);
    }

    if (!Object.keys(summary).length) {
      summary.responsePreview = safeStringifyForLog(responseData, LOG_BODY_LIMIT);
    }

    return summary;
  }

  return {
    responsePreview: safeStringifyForLog(responseData, LOG_BODY_LIMIT),
  };
}

export function buildFetchDebugContext({
  headers = {},
  requestEvent = null,
  hostSite = "",
  currentServer = "",
  requestUrl = "",
  fetchOptions = {},
  isMallRequest = false,
  responseStatusCode = "",
  responseData,
}) {
  const requestMethod = String(fetchOptions?.method || "GET").toUpperCase();

  return {
    requestId: buildRequestLogId(headers, requestEvent),
    runtime: process.server ? "server" : "client",
    ssrPath: process.server ? truncateLogValue(requestEvent?.node?.req?.url || "", LOG_BODY_LIMIT) : "",
    ssrMethod: process.server ? truncateLogValue(requestEvent?.node?.req?.method || "", LOG_VALUE_LIMIT) : "",
    currentServer: truncateLogValue(currentServer || getCurrentServer(headers), LOG_VALUE_LIMIT),
    siteHost: truncateLogValue(hostSite || getCurrentSiteHost(headers), LOG_VALUE_LIMIT),
    host: truncateLogValue(getHeaderValue(headers, "host"), LOG_VALUE_LIMIT),
    xForwardedHost: truncateLogValue(getHeaderValue(headers, "x-forwarded-host"), LOG_VALUE_LIMIT),
    xCustomHeader1: truncateLogValue(getHeaderValue(headers, "x-custom-header1"), LOG_VALUE_LIMIT),
    xForwardedProto: truncateLogValue(getHeaderValue(headers, "x-forwarded-proto"), LOG_VALUE_LIMIT),
    userAgent: truncateLogValue(getHeaderValue(headers, "user-agent"), LOG_VALUE_LIMIT),
    requestMethod,
    requestUrl: truncateLogValue(requestUrl, LOG_BODY_LIMIT),
    baseURL: truncateLogValue(fetchOptions?.baseURL || "", LOG_BODY_LIMIT),
    upstreamUrl: truncateLogValue(
      buildUpstreamUrl(fetchOptions?.baseURL, requestUrl),
      LOG_BODY_LIMIT,
    ),
    isMallRequest: Boolean(isMallRequest),
    authorizationPresent: Boolean(getHeaderValue(fetchOptions?.headers, "authorization")),
    openaccessjwtPresent: Boolean(getHeaderValue(fetchOptions?.headers, "openaccessjwt")),
    responseStatusCode: responseStatusCode || "",
    ...summarizeResponseForLog(responseData),
  };
}

export function attachFetchDebugContext(error, context = {}) {
  if (!error || typeof error !== "object") {
    return error;
  }

  error.fetchDebugContext = {
    ...(error.fetchDebugContext || {}),
    ...context,
  };

  return error;
}

export function logCustomFetchError(error) {
  if (error && typeof error === "object" && error.fetchDebugLogged) {
    return;
  }

  if (error && typeof error === "object") {
    error.fetchDebugLogged = true;
  }

  console.error(
    JSON.stringify({
      type: "nuxt:custom-fetch-error",
      errorName: truncateLogValue(error?.name || "Error", LOG_VALUE_LIMIT),
      errorMessage: truncateLogValue(error?.message || "请求失败", LOG_BODY_LIMIT),
      stackPreview: safeStringifyForLog(error?.stack, LOG_STACK_LIMIT),
      ...(error?.fetchDebugContext || {}),
    }),
  );
}

export function buildStoreFetchErrorLog(action, headers = {}, error, extra = {}) {
  const fetchDebugContext = error?.fetchDebugContext || {};

  return {
    type: "nuxt:ssr-bootstrap-error",
    action,
    requestId: fetchDebugContext.requestId || truncateLogValue(getHeaderValue(headers, "x-request-id")),
    runtime: fetchDebugContext.runtime || (process.server ? "server" : "client"),
    ssrPath: fetchDebugContext.ssrPath || "",
    currentServer: truncateLogValue(
      extra.currentServer || fetchDebugContext.currentServer || getCurrentServer(headers),
      LOG_VALUE_LIMIT,
    ),
    siteHost: truncateLogValue(
      fetchDebugContext.siteHost || getCurrentSiteHost(headers),
      LOG_VALUE_LIMIT,
    ),
    xCustomHeader1: truncateLogValue(
      fetchDebugContext.xCustomHeader1 || getHeaderValue(headers, "x-custom-header1"),
      LOG_VALUE_LIMIT,
    ),
    upstreamUrl: truncateLogValue(fetchDebugContext.upstreamUrl || "", LOG_BODY_LIMIT),
    upstreamStatusCode: fetchDebugContext.responseStatusCode || "",
    upstreamBusinessStatus: truncateLogValue(
      fetchDebugContext.responseBusinessStatus || "",
      LOG_VALUE_LIMIT,
    ),
    upstreamBusinessCode: truncateLogValue(
      fetchDebugContext.responseBusinessCode || "",
      LOG_VALUE_LIMIT,
    ),
    upstreamBusinessMessage: truncateLogValue(
      fetchDebugContext.responseBusinessMessage || "",
      LOG_VALUE_LIMIT,
    ),
    responsePreview: truncateLogValue(fetchDebugContext.responsePreview || "", LOG_BODY_LIMIT),
    errorName: truncateLogValue(error?.name || "Error", LOG_VALUE_LIMIT),
    errorMessage: truncateLogValue(error?.message || "请求失败", LOG_BODY_LIMIT),
    ...extra,
  };
}

export function logStoreFetchError(action, headers, error, extra = {}) {
  console.error(JSON.stringify(buildStoreFetchErrorLog(action, headers, error, extra)));
}
